/**
 * Adapter legacy (CF02) — il ponte fra ciò che è memorizzato e il contratto.
 *
 * Le due forme esistenti, verificate sul database condiviso prima di scrivere
 * questo file:
 *
 * - `processmodels.userschema` (jsonb): un elenco di `{id, name, type, inCard}`.
 *   `type` è l'enum `0=text, 1=number, 2=date, 3=boolean`. Sia `[]` sia `{}`
 *   compaiono e **significano entrambi "nessun campo"**: 6602 modelli hanno la
 *   forma a oggetto, quindi trattarla come malformata manderebbe migliaia di
 *   modelli in coda di correzione per niente.
 * - `processes.userdata` (text): un oggetto con **le chiavi che sono i nomi
 *   storici dei campi**, `{"Importo presunto":"10000"}`. I valori sono quindi
 *   indirizzati dalla label — ed è esattamente per questo che il contratto
 *   porta `legacyKey` accanto a `label`: una rinomina non deve orfanare il
 *   dato già memorizzato.
 *
 * Qui non c'è nessuna regola autorevole: conversione e segnalazione. Ciò che
 * non si sa convertire viene **riportato**, mai sostituito in silenzio con una
 * mappa vuota — `getUserData` fa proprio questo oggi, e un salvataggio dopo
 * quella lettura cancellerebbe il dato che descrive.
 */

import type { FieldDefinition, FieldValue } from "model/CustomFields";

/** L'enum legacy. I suoi ordinali fanno parte del dato: non si rinumerano. */
export const LEGACY_TYPE_ENUM: Readonly<Record<number, string>> = {
  0: "text",
  1: "number",
  2: "date",
  3: "boolean",
} as const;

/** Decimali canonici: nessun esponente, nessuno zero frazionario finale. */
const CANONICAL_DECIMAL = /^-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const TRUE_LITERALS = new Set(["true", "1"]);
const FALSE_LITERALS = new Set(["false", "0"]);

/** Le proprietà che la forma legacy definisce. Tutto il resto è un extra. */
const KNOWN_FIELD_KEYS = new Set(["id", "name", "type", "inCard"]);

export interface LegacyIssue {
  code: string;
  /** Dove: `userschema[3]` oppure `userdata.Importo`. Mai il valore. */
  location: string;
  detail?: string;
}

/**
 * La riga legacy come e' memorizzata in `userschema`.
 *
 * Prima viveva dentro il dialog della master; ora sta qui, accanto alle due
 * conversioni che la riguardano, cosi' il tipo ha una sola casa.
 */
export interface LegacySchemaRow {
  type: number;
  name: string;
  id: string;
  inCard: boolean;
}

export interface LegacyField {
  fieldId: string;
  /** Il nome storico. I valori restano indirizzati da questo, non dalla label. */
  legacyKey: string;
  label: string;
  fieldType: string;
  inCard: boolean;
  position: number;
  /** Proprietà sconosciute: conservate, non interpretate, non mostrate. */
  extras: Record<string, unknown>;
}

export interface LegacySchema {
  fields: LegacyField[];
  issues: LegacyIssue[];
  /** Il payload così com'è stato letto: un dato illeggibile resta leggibile. */
  original: unknown;
}

export interface LegacyValues {
  /** fieldId -> valore canonico. Ogni campo dichiarato compare, `null` se assente. */
  values: Record<string, FieldValue>;
  issues: LegacyIssue[];
  /** Chiavi che non corrispondono a nessun campo: conservate, non mostrate. */
  extras: Record<string, unknown>;
  original: unknown;
}

/** Conta i code point, non le unità UTF-16: un'emoji è un carattere. */
export const codePointLength = (value: string): number => Array.from(value).length;

/**
 * Converte un valore legacy nel valore del contratto.
 *
 * Il tipo decide come leggere la stringa, e **un testo non viene mai
 * convertito**: `"false"` in un campo di testo resta il testo `"false"`, e
 * `"0"` in un campo numerico resta la stringa canonica `"0"`. `"false"` è
 * falso soltanto dove il campo è booleano.
 */
export function normalizeLegacyValue(
  type: number,
  value: string | null | undefined
): FieldValue {
  const fieldType = LEGACY_TYPE_ENUM[type];
  if (!fieldType) {
    throw new Error(`Tipo legacy sconosciuto: ${String(type)}`);
  }
  if (value === null || value === undefined) return null;

  if (fieldType === "text") {
    // Il testo conserva il contenuto, "false" e "0" compresi: sono ciò che
    // l'utente ha scritto, non un booleano né un numero.
    return value;
  }

  if (value.trim() === "") return null;

  if (fieldType === "number") return canonicalDecimal(value);
  if (fieldType === "boolean") {
    const lowered = value.trim().toLowerCase();
    if (TRUE_LITERALS.has(lowered)) return true;
    if (FALSE_LITERALS.has(lowered)) return false;
    return null;
  }
  if (fieldType === "date") return isoDate(value);
  return null;
}

/**
 * Normalizza un decimale, o restituisce `null`.
 *
 * `1.000,50` è inequivocabile per una persona e ambiguo per una macchina:
 * scegliere un locale sarebbe un'ipotesi, e un'ipotesi sbagliata cambia
 * silenziosamente l'importo.
 */
function canonicalDecimal(raw: string): FieldValue {
  const candidate = raw.trim();
  if (!CANONICAL_DECIMAL.test(candidate)) return null;
  if (candidate === "-0" || candidate === "-0.00") return "0";

  // Gli zeri frazionari finali cadono: `24.00` e `24` sono lo stesso numero e
  // il trasporto ne porta una sola grafia.
  if (candidate.includes(".")) {
    const trimmed = candidate.replace(/0+$/, "").replace(/\.$/, "");
    return trimmed === "" || trimmed === "-" ? "0" : trimmed;
  }
  return candidate;
}

/** Una data ISO, o un istante ISO con offset. Nessun locale indovinato. */
function isoDate(raw: string): FieldValue {
  const candidate = raw.trim();
  if (ISO_DATE.test(candidate)) {
    const parsed = new Date(`${candidate}T00:00:00Z`);
    return Number.isNaN(parsed.getTime()) ? null : candidate;
  }
  // Un istante senza offset non è collocabile su una linea temporale.
  if (!/(?:[+-]\d{2}:\d{2}|Z)$/.test(candidate)) return null;
  const parsed = new Date(candidate);
  return Number.isNaN(parsed.getTime()) ? null : candidate;
}

/** `JSON.parse` che non lancia, con l'indicazione se ha funzionato. */
function loadJson(raw: unknown): { parsed: unknown; readable: boolean } {
  if (typeof raw === "string") {
    try {
      return { parsed: JSON.parse(raw), readable: true };
    } catch {
      return { parsed: raw, readable: false };
    }
  }
  return { parsed: raw, readable: true };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Converte uno `userschema` memorizzato in campi.
 *
 * `labels` permette di applicare una rinomina già persistita: la label cambia,
 * `legacyKey` e `fieldId` no — ed è ciò che tiene raggiungibili i valori.
 */
export function parseLegacySchema(
  raw: unknown,
  options: { modelId: string; labels?: Record<string, string> } = { modelId: "" }
): LegacySchema {
  const { parsed, readable } = loadJson(raw);
  const unreadable = (): LegacySchema => ({
    fields: [],
    issues: [{ code: "legacy_schema_unreadable", location: "userschema" }],
    original: parsed,
  });

  if (!readable) return unreadable();

  // `[]` e `{}` significano entrambi "nessun campo", e non sono un errore.
  if (parsed === null || parsed === undefined) {
    return { fields: [], issues: [], original: parsed };
  }
  if (Array.isArray(parsed) && parsed.length === 0) {
    return { fields: [], issues: [], original: parsed };
  }
  if (isRecord(parsed) && Object.keys(parsed).length === 0) {
    return { fields: [], issues: [], original: parsed };
  }
  // Un oggetto non vuoto non è una forma che questa versione conosce.
  if (!Array.isArray(parsed)) return unreadable();

  const labels = options.labels ?? {};
  const issues: LegacyIssue[] = [];
  const fields: LegacyField[] = [];
  const seenIds = new Set<string>();
  const seenNames = new Set<string>();

  parsed.forEach((item, index) => {
    const location = `userschema[${index}]`;
    if (!isRecord(item)) {
      issues.push({ code: "legacy_schema_unreadable", location });
      return;
    }

    const name = item.name;
    if (typeof name !== "string" || name.trim() === "") {
      issues.push({ code: "legacy_field_missing_name", location });
      return;
    }

    const fieldType = LEGACY_TYPE_ENUM[Number(item.type)];
    if (!fieldType) {
      issues.push({
        code: "legacy_field_unknown_type",
        location,
        detail: `type=${String(item.type)}`,
      });
      return;
    }

    const rawId = item.id;
    let fieldId =
      typeof rawId === "string" && rawId.trim() !== "" ? rawId : null;
    if (fieldId === null) {
      // Derivato in modo deterministico: un id casuale orfanerebbe i valori
      // al primo salvataggio successivo.
      fieldId = `legacy-${options.modelId}-${name}`;
      issues.push({ code: "legacy_field_missing_id", location });
    } else if (seenIds.has(fieldId)) {
      issues.push({ code: "legacy_field_duplicate_id", location });
      return;
    }

    if (seenNames.has(name)) {
      // Entrambi i duplicati restano: il progettista deve vederli per scegliere.
      issues.push({ code: "legacy_field_duplicate_name", location });
    }

    seenIds.add(fieldId);
    seenNames.add(name);

    fields.push({
      fieldId,
      legacyKey: name,
      label: labels[fieldId] ?? name,
      fieldType,
      inCard: item.inCard === true,
      position: fields.length,
      extras: Object.fromEntries(
        Object.entries(item).filter(([key]) => !KNOWN_FIELD_KEYS.has(key))
      ),
    });
  });

  return { fields, issues, original: parsed };
}

/**
 * Converte uno `userdata` memorizzato in valori indirizzati per `fieldId`.
 *
 * Le chiavi che non corrispondono a nessun campo finiscono in `extras`: non
 * è una ragione per scartare il dato, e non è una ragione per mostrarlo.
 */
export function parseLegacyValues(raw: unknown, schema: LegacySchema): LegacyValues {
  const { parsed, readable } = loadJson(raw);
  const unreadable = (): LegacyValues => ({
    values: Object.fromEntries(schema.fields.map((f) => [f.fieldId, null])),
    issues: [{ code: "legacy_userdata_unreadable", location: "userdata" }],
    extras: {},
    original: parsed,
  });

  if (!readable) return unreadable();

  const data = parsed === null || parsed === undefined ? {} : parsed;
  if (!isRecord(data)) return unreadable();

  const issues: LegacyIssue[] = [];
  const values: Record<string, FieldValue> = {};
  const consumed = new Set<string>();

  for (const field of schema.fields) {
    consumed.add(field.legacyKey);
    const rawValue = data[field.legacyKey];
    if (rawValue === undefined || rawValue === null) {
      values[field.fieldId] = null;
      continue;
    }
    // Il tipo legacy del campo decide come si legge; un valore che non si sa
    // convertire è `null` e viene **riportato**.
    const legacyType = Number(
      Object.keys(LEGACY_TYPE_ENUM).find(
        (key) => LEGACY_TYPE_ENUM[Number(key)] === field.fieldType
      ) ?? -1
    );
    const converted = normalizeLegacyValue(legacyType, String(rawValue));
    values[field.fieldId] = converted;
    if (
      converted === null &&
      String(rawValue).trim() !== "" &&
      field.fieldType !== "text"
    ) {
      issues.push({
        code: "legacy_value_not_convertible",
        location: `userdata.${field.legacyKey}`,
        detail: `atteso ${field.fieldType}`,
      });
    }
  }

  const extras = Object.fromEntries(
    Object.entries(data).filter(([key]) => !consumed.has(key))
  );
  for (const key of Object.keys(extras)) {
    issues.push({ code: "legacy_value_orphan_key", location: `userdata.${key}` });
  }

  return { values, issues, extras, original: parsed };
}

/**
 * La patch differenziale da inviare.
 *
 * Solo i campi **editabili** che sono davvero cambiati. Un campo omesso resta
 * invariato e `null` cancella; rimandare indietro la mappa intera
 * sovrascriverebbe i valori che il chiamante non può vedere con il sottoinsieme
 * che vede — la perdita silenziosa che il contratto vieta.
 *
 * Il confronto è per valore, non per identità: due array con gli stessi
 * elementi sono lo stesso valore, e rispedirli non è una modifica.
 */
export function buildChanges(
  before: Record<string, FieldValue | undefined>,
  after: Record<string, FieldValue | undefined>,
  editableIds: readonly string[]
): Record<string, FieldValue> {
  const changes: Record<string, FieldValue> = {};
  for (const fieldId of editableIds) {
    if (!Object.prototype.hasOwnProperty.call(after, fieldId)) continue;
    if (valuesEqual(before[fieldId], after[fieldId])) continue;
    changes[fieldId] = after[fieldId] as FieldValue;
  }
  return changes;
}

/** Uguaglianza per valore, con gli array confrontati elemento per elemento. */
export function valuesEqual(
  left: FieldValue | undefined,
  right: FieldValue | undefined
): boolean {
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length && left.every((item, i) => item === right[i])
    );
  }
  return left === right;
}


// ---------------------------------------------------------------------------
// Proiezione verso la forma legacy (transitoria)
// ---------------------------------------------------------------------------

/**
 * I tipi del contratto con la loro resa legacy, quando esiste.
 *
 * La forma legacy conosce quattro tipi: tutto il resto deve essere ricondotto, e
 * una riduzione non e' una conversione. `null` significa che il tipo non ha una
 * resa fedele e va segnalato invece che appiattito in silenzio.
 */
const LEGACY_EQUIVALENT: Readonly<Record<string, number | null>> = {
  text: 0,
  textarea: 0,
  email: 0,
  url: 0,
  phone: 0,
  select: 0,
  radio: 0,
  number: 1,
  currency: 1,
  percent: 1,
  date: 2,
  boolean: 3,
  // Il valore di un progressivo e' una stringa, ma il campo non e' scrivibile in
  // legacy: nessuna resa fedele.
  autoincrement: null,
  // Un calcolato dipende dal tipo dichiarato: deciso caso per caso.
  calculated: null,
  // Una selezione multipla e' un array; la forma legacy porta una stringa sola.
  multiselect: null,
};

export interface LegacyProjection {
  /** Le righe nella forma legacy, per i lettori che ancora la usano. */
  rows: LegacySchemaRow[];
  /** Gli id dei campi che la forma legacy non sa rappresentare fedelmente. */
  unrepresentable: string[];
}

/**
 * Proietta le definizioni del contratto nella forma legacy.
 *
 * **Transitoria.** Le specifiche vietano al client di aggiornare `userschema` e
 * il nuovo schema separatamente: la proiezione appartiene al server. Finche' il
 * bridge che la deriva non esiste, il client la calcola per non interrompere i
 * lettori legacy (card, filtri, statistiche, adapter Python) — e va rimossa
 * quando il server la fornisce.
 *
 * `name` e' la **chiave legacy** quando c'e', non la label: i valori sono
 * indirizzati dal nome storico, quindi usare la label dopo una rinomina
 * orfanerebbe il dato nelle superfici non ancora migrate. Una label vecchia e'
 * cosmetica, un valore perso no.
 */
export function toLegacySchema(
  fields: readonly {
    fieldId: string;
    label: string;
    type: string;
    legacyKey?: string | null;
    inCard?: boolean;
    deleted?: boolean;
    config?: Record<string, unknown>;
  }[]
): LegacyProjection {
  const rows: LegacyProjection["rows"] = [];
  const unrepresentable: string[] = [];

  for (const field of fields) {
    if (field.deleted) continue;

    const equivalent =
      field.type === "calculated"
        ? LEGACY_EQUIVALENT[String(field.config?.outputType ?? "number")]
        : LEGACY_EQUIVALENT[field.type];

    if (equivalent === null || equivalent === undefined) {
      // Segnalato, non appiattito: un tipo ridotto a un altro cambierebbe il
      // significato di ogni valore gia' memorizzato sotto quel campo.
      unrepresentable.push(field.fieldId);
      continue;
    }

    rows.push({
      type: equivalent,
      name: field.legacyKey ?? field.label,
      id: field.fieldId,
      inCard: field.inCard === true,
    });
  }

  return { rows, unrepresentable };
}


// ---------------------------------------------------------------------------
// Dalla forma legacy al contratto
// ---------------------------------------------------------------------------

/**
 * Converte un campo legacy nella definizione del contratto.
 *
 * E' il verso che permette alla master di lavorare sui `FieldDefinition` mentre
 * cio' che e' memorizzato resta nella forma legacy: si legge legacy, si lavora
 * nel contratto, si riproietta al salvataggio.
 *
 * Tre scelte:
 *
 * - `legacyKey` **resta** il nome storico: e' l'indirizzo dei valori, e non
 *   cambia mai. `label` e' cio' che si mostra, e da qui in poi e' modificabile
 *   senza toccare l'indirizzo.
 * - visibilita' `process`/`editable`, perche' la forma legacy non sa esprimerne
 *   altre: restringerla e' una decisione che il progettista prende dopo, con
 *   l'analisi degli impatti.
 * - obbligatorieta' spenta, per lo stesso motivo: accenderla cambierebbe il
 *   comportamento dei processi gia' avviati, e non deve succedere per omissione.
 */
export function legacyToField(legacy: LegacyField): FieldDefinition {
  return {
    fieldId: legacy.fieldId,
    legacyKey: legacy.legacyKey,
    label: legacy.label,
    type: legacy.fieldType as FieldDefinition["type"],
    position: legacy.position,
    inCard: legacy.inCard,
    requiredOnTask: false,
    requiredOnProcess: false,
    visibility: { scope: "process", access: "editable" },
    config: {},
    deleted: false,
  };
}


/**
 * Converte righe legacy in definizioni del contratto.
 *
 * Il verso che serve al confine di caricamento: la master lavora su
 * `FieldDefinition`, ma cio' che arriva da `userschema` — e da `getUserSchema`
 * — e' la riga legacy.
 *
 * `name` diventa **sia** `legacyKey` sia `label`: nella forma legacy sono la
 * stessa cosa, ed e' la separazione che il contratto introduce. Da qui in poi
 * la label si puo' cambiare senza toccare l'indirizzo dei valori.
 */
export function rowsToFields(
  rows: readonly LegacySchemaRow[]
): FieldDefinition[] {
  return rows.map((row, index) => ({
    fieldId: row.id,
    legacyKey: row.name,
    label: row.name,
    type: (LEGACY_TYPE_ENUM[row.type] ?? "text") as FieldDefinition["type"],
    position: index,
    inCard: row.inCard === true,
    // Obbligatorieta' e visibilita' restano spente: la forma legacy non sa
    // esprimerle, e accenderle per omissione cambierebbe il comportamento dei
    // processi gia' avviati.
    requiredOnTask: false,
    requiredOnProcess: false,
    visibility: { scope: "process", access: "editable" },
    config: {},
    deleted: false,
  }));
}
